import { AxiosResponse } from 'axios'
import { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import * as z from 'zod'
import { UserOptionsMe } from '~/shared/api/core/store'
import { buildI18nMessage } from '~/shared/lib/i18n'
import {
  dateSchema,
  enumOptionSchema,
  phoneScheme,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { LocaleEnum } from '../config/enums'
import { ApiModel } from './core'

const attributeSchema = z.object({
  email: z.string().email(buildI18nMessage('validate.invalid email address')),
  password: z.string().min(5, {
    message: buildI18nMessage(
      'validate.password must be at least 5 characters long',
    ),
  }),
  name: z.string(),
  phone: phoneScheme,
  telegramUserId: z.number().nullable(),
  locale: enumOptionSchema(LocaleEnum),
})

const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema,
  rolesIds: uuidOptionsSchema,
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type UserAttributes = z.infer<typeof schema>

export class User extends ApiModel<typeof schema> {
  static jsonApiType = 'users'

  static schema = schema

  getTitle(): string {
    return this.getAttribute('name')
  }

  static async login(credentials: { email: Email; password: string }) {
    const url = `${this.getJsonApiUrl()}/login`
    const client = this.httpClient.getImplementingClient()
    return client
      .post<
        typeof credentials,
        AxiosResponse<{ token: string; id: UniqueId; email: string }>
      >(url, credentials, {
        skipAuthRefresh: true,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
        },
      } as AxiosAuthRefreshRequestConfig)
      .then(({ data }) => data)
  }

  static async me() {
    const url = `${this.getJsonApiUrl()}/me`
    const client = User.httpClient.getImplementingClient()
    return client.get<UserOptionsMe>(url).then(({ data }) => data)
  }

  static async logout() {
    const url = `${this.getJsonApiUrl()}/logout`
    return User.effectiveHttpClient.post(url)
  }

  getEmail(): string {
    return this.getAttribute('email')
  }

  getName(): string {
    return this.getAttribute('name')
  }

  getPhone(): string | undefined {
    return this.getAttribute('phone')
  }
}
